import { baseUrl } from 'api/config'
import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { GeneratedRoadmap } from './AIRoadmapResponse'
import {
  CatalogItem,
  classifyItem,
  CS_RESERVE_PCT,
  ENGAGEMENT_PRESETS,
  EngagementConfig,
  EngagementDuration,
  LineItem,
  normalizeServiceLine,
  Phase,
  PhaseSummary,
  RoadmapState,
  RoadmapView,
  SERVICE_LINE_LABELS,
  ServiceLineSummary,
} from './types'

// ─── State ─────────────────────────────────────────────────────────

const defaultEngagement: EngagementConfig = {
  duration: 12,
  includeSetup: true,
  monthlyBudget: 10,
  setupBudget: 15,
  discountPercent: 0,
  budgetMode: 'dollars',
  monthlyDollarBudget: 2000,
  setupDollarBudget: 3000,
  startMonth: new Date().toISOString().slice(0, 7),
}

const initialState: RoadmapState = {
  view: 'setup',
  catalog: [],
  isLoading: false,
  engagementConfig: defaultEngagement,
  enabledServiceLines: new Set<string>(),
  lineItems: [],
  activePhase: 'setup',
  clientName: '',
  clientIndustry: '',
  clientSize: '',
  clientOrgId: '',
  businessModel: '',
  objectives: new Set<string>(),
  clientWebsite: '',
  salesNotes: '',
  aiInstructions: '',
  recommendedSkus: new Map(),
  isRecommending: false,
  recommendationSummary: '',
  generatedRoadmap: null,
  isGeneratingRoadmap: false,
  roadmapAccepted: false,
}

// ─── Actions ───────────────────────────────────────────────────────

type Action =
  | { type: 'SET_VIEW'; view: RoadmapView }
  | { type: 'SET_CATALOG'; catalog: CatalogItem[] }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ENGAGEMENT'; config: Partial<EngagementConfig> }
  | { type: 'SET_DURATION'; duration: EngagementDuration }
  | { type: 'TOGGLE_SERVICE_LINE'; code: string }
  | { type: 'SET_SERVICE_LINES'; codes: Set<string> }
  | { type: 'ADD_ITEM'; item: CatalogItem; phase: Phase }
  | { type: 'REMOVE_ITEM'; lineItemId: string }
  | { type: 'MOVE_ITEM'; lineItemId: string; phase: Phase }
  | { type: 'UPDATE_QUANTITY'; lineItemId: string; quantity: number }
  | { type: 'SET_ACTIVE_PHASE'; phase: Phase }
  | { type: 'SET_CLIENT_NAME'; name: string }
  | { type: 'SET_CLIENT_INDUSTRY'; industry: string }
  | { type: 'SET_CLIENT_SIZE'; size: 'smb' | 'mid_market' | 'enterprise' | '' }
  | { type: 'SET_CLIENT_ORG'; name: string; orgId: string; industry: string }
  | { type: 'SET_BUSINESS_MODEL'; model: 'lead_gen' | 'ecommerce' | 'both' | '' }
  | { type: 'TOGGLE_OBJECTIVE'; objective: string }
  | { type: 'SET_CLIENT_WEBSITE'; website: string }
  | { type: 'SET_SALES_NOTES'; notes: string }
  | { type: 'SET_AI_INSTRUCTIONS'; instructions: string }
  | { type: 'CLEAR_LINE_ITEMS' }
  | { type: 'RESET_CLIENT_PROFILE' }
  | { type: 'SET_RECOMMENDING'; isRecommending: boolean }
  | { type: 'SET_RECOMMENDATIONS'; skus: Map<string, string>; summary: string }
  | { type: 'SET_GENERATING_ROADMAP'; isGenerating: boolean }
  | { type: 'SET_GENERATED_ROADMAP'; roadmap: GeneratedRoadmap }
  | { type: 'ACCEPT_ROADMAP' }
  | { type: 'CLEAR_GENERATED_ROADMAP' }

function reducer(state: RoadmapState, action: Action): RoadmapState {
  switch (action.type) {
    case 'SET_VIEW':
      return { ...state, view: action.view }
    case 'SET_CATALOG':
      return { ...state, catalog: action.catalog, isLoading: false }
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading }
    case 'SET_ENGAGEMENT':
      return { ...state, engagementConfig: { ...state.engagementConfig, ...action.config } }
    case 'SET_DURATION': {
      const preset = ENGAGEMENT_PRESETS.find(p => p.duration === action.duration)
      const rate = preset?.ratePerPoint || 200
      const cfg = state.engagementConfig
      const monthlyPts = cfg.budgetMode === 'dollars' ? Math.round(cfg.monthlyDollarBudget / rate) : cfg.monthlyBudget
      const setupPts = cfg.budgetMode === 'dollars' ? Math.round(cfg.setupDollarBudget / rate) : cfg.setupBudget
      return {
        ...state,
        engagementConfig: {
          ...cfg,
          duration: action.duration,
          includeSetup: action.duration > 1,
          monthlyBudget: monthlyPts,
          setupBudget: setupPts,
        },
      }
    }
    case 'TOGGLE_SERVICE_LINE': {
      const next = new Set(state.enabledServiceLines)
      if (next.has(action.code)) next.delete(action.code)
      else next.add(action.code)
      return { ...state, enabledServiceLines: next }
    }
    case 'SET_SERVICE_LINES':
      return { ...state, enabledServiceLines: action.codes }
    case 'ADD_ITEM': {
      // Prevent duplicates in same phase
      const exists = state.lineItems.some(
        li => li.products_uuid === action.item.products_uuid && li.phase === action.phase
      )
      if (exists) return state
      const newItem: LineItem = {
        ...action.item,
        lineItemId: uuidv4(),
        phase: action.phase,
        quantity: 1,
        category: classifyItem(action.item),
      }
      return { ...state, lineItems: [...state.lineItems, newItem] }
    }
    case 'REMOVE_ITEM':
      return { ...state, lineItems: state.lineItems.filter(li => li.lineItemId !== action.lineItemId) }
    case 'MOVE_ITEM':
      return {
        ...state,
        lineItems: state.lineItems.map(li =>
          li.lineItemId === action.lineItemId ? { ...li, phase: action.phase } : li
        ),
      }
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        lineItems: state.lineItems.map(li =>
          li.lineItemId === action.lineItemId ? { ...li, quantity: Math.max(1, action.quantity) } : li
        ),
      }
    case 'SET_ACTIVE_PHASE':
      return { ...state, activePhase: action.phase }
    case 'SET_CLIENT_NAME':
      return { ...state, clientName: action.name }
    case 'SET_CLIENT_INDUSTRY':
      return { ...state, clientIndustry: action.industry }
    case 'SET_CLIENT_SIZE':
      return { ...state, clientSize: action.size }
    case 'SET_CLIENT_ORG':
      return { ...state, clientName: action.name, clientOrgId: action.orgId, clientIndustry: action.industry }
    case 'SET_BUSINESS_MODEL':
      return { ...state, businessModel: action.model }
    case 'TOGGLE_OBJECTIVE': {
      const next = new Set(state.objectives)
      if (next.has(action.objective)) next.delete(action.objective)
      else next.add(action.objective)
      return { ...state, objectives: next }
    }
    case 'SET_CLIENT_WEBSITE':
      return { ...state, clientWebsite: action.website }
    case 'SET_SALES_NOTES':
      return { ...state, salesNotes: action.notes }
    case 'SET_AI_INSTRUCTIONS':
      return { ...state, aiInstructions: action.instructions }
    case 'CLEAR_LINE_ITEMS':
      return { ...state, lineItems: [] }
    case 'RESET_CLIENT_PROFILE':
      return {
        ...state,
        clientName: '',
        clientOrgId: '',
        clientIndustry: '',
        clientSize: '',
        businessModel: '',
        objectives: new Set<string>(),
        clientWebsite: '',
        salesNotes: '',
        aiInstructions: '',
        recommendedSkus: new Map(),
        isRecommending: false,
        recommendationSummary: '',
      }
    case 'SET_RECOMMENDING':
      return { ...state, isRecommending: action.isRecommending }
    case 'SET_RECOMMENDATIONS':
      return { ...state, recommendedSkus: action.skus, recommendationSummary: action.summary, isRecommending: false }
    case 'SET_GENERATING_ROADMAP':
      return { ...state, isGeneratingRoadmap: action.isGenerating }
    case 'SET_GENERATED_ROADMAP':
      return { ...state, generatedRoadmap: action.roadmap, isGeneratingRoadmap: false, roadmapAccepted: false }
    case 'ACCEPT_ROADMAP':
      return { ...state, roadmapAccepted: true }
    case 'CLEAR_GENERATED_ROADMAP':
      return { ...state, generatedRoadmap: null, isGeneratingRoadmap: false, roadmapAccepted: false }
    default:
      return state
  }
}

// ─── Context ───────────────────────────────────────────────────────

interface RoadmapContextValue {
  state: RoadmapState
  dispatch: React.Dispatch<Action>
  // Computed
  phases: Phase[]
  phaseSummaries: PhaseSummary[]
  serviceLineSummaries: ServiceLineSummary[]
  ratePerPoint: number
  effectiveRate: number
  totalPoints: number
  totalInvestment: number
  // Actions
  addItem: (item: CatalogItem, phase?: Phase) => void
  removeItem: (lineItemId: string) => void
  moveItem: (lineItemId: string, phase: Phase) => void
  fetchRecommendations: () => Promise<void>
  fetchRoadmap: () => Promise<void>
  reviseRoadmap: (instructions: string) => Promise<void>
  clearGeneratedRoadmap: () => void
  acceptRoadmap: () => void
  acceptRoadmapPhase: (phase: string) => void
}

const RoadmapContext = createContext<RoadmapContextValue | null>(null)

export function useRoadmap() {
  const ctx = useContext(RoadmapContext)
  if (!ctx) throw new Error('useRoadmap must be used within RoadmapProvider')
  return ctx
}

interface RoadmapProviderProps {
  children: React.ReactNode
  initialClientName?: string
  initialClientOrgId?: string
  initialClientIndustry?: string
}

export function RoadmapProvider({
  children,
  initialClientName,
  initialClientOrgId,
  initialClientIndustry,
}: RoadmapProviderProps) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    clientName: initialClientName ?? initialState.clientName,
    clientOrgId: initialClientOrgId ?? initialState.clientOrgId,
    clientIndustry: initialClientIndustry ?? initialState.clientIndustry,
  })

  const ratePerPoint = useMemo(() => {
    return ENGAGEMENT_PRESETS.find(p => p.duration === state.engagementConfig.duration)?.ratePerPoint || 200
  }, [state.engagementConfig.duration])

  const effectiveRate = useMemo(() => {
    const discount = state.engagementConfig.discountPercent / 100
    return ratePerPoint * (1 - discount)
  }, [ratePerPoint, state.engagementConfig.discountPercent])

  const phases = useMemo<Phase[]>(() => {
    const result: Phase[] = []
    if (state.engagementConfig.includeSetup) result.push('setup')
    for (let i = 1; i <= state.engagementConfig.duration; i++) {
      result.push(`month-${i}` as Phase)
    }
    return result
  }, [state.engagementConfig.duration, state.engagementConfig.includeSetup])

  const phaseSummaries = useMemo<PhaseSummary[]>(() => {
    const { monthlyBudget, setupBudget } = state.engagementConfig
    const effectiveMonthly = Math.round(monthlyBudget * (1 - CS_RESERVE_PCT))

    return phases.map(phase => {
      const budget = phase === 'setup' ? setupBudget : effectiveMonthly
      const items = state.lineItems.filter(li => li.phase === phase)
      const usedPoints = items.reduce((sum, li) => sum + li.points * li.quantity, 0)

      return {
        phase,
        label: phase === 'setup' ? 'Setup' : `Month ${phase.replace('month-', '')}`,
        budget,
        usedPoints,
        utilization: budget > 0 ? Math.round((usedPoints / budget) * 100) : 0,
        itemCount: items.length,
        strategicCount: items.filter(li => li.category === 'strategic').length,
        executableCount: items.filter(li => li.category === 'executable').length,
        isOverBudget: usedPoints > budget,
      }
    })
  }, [phases, state.lineItems, state.engagementConfig])

  const serviceLineSummaries = useMemo<ServiceLineSummary[]>(() => {
    const groups: Record<string, CatalogItem[]> = {}
    for (const item of state.catalog) {
      const sl = normalizeServiceLine(item.service_line)
      if (!groups[sl]) groups[sl] = []
      groups[sl].push(item)
    }
    return Object.entries(groups)
      .map(([code, items]) => ({
        code,
        label: SERVICE_LINE_LABELS[code] || code.toUpperCase(),
        itemCount: items.length,
        minPoints: Math.min(...items.map(i => i.points || 0)),
        maxPoints: Math.max(...items.map(i => i.points || 0)),
        enabled: state.enabledServiceLines.has(code),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [state.catalog, state.enabledServiceLines])

  const totalPoints = useMemo(() => {
    return state.lineItems.reduce((sum, li) => sum + li.points * li.quantity, 0)
  }, [state.lineItems])

  const totalInvestment = useMemo(() => totalPoints * effectiveRate, [totalPoints, effectiveRate])

  const addItem = useCallback(
    (item: CatalogItem, phase?: Phase) => {
      dispatch({ type: 'ADD_ITEM', item, phase: phase || state.activePhase })
    },
    [state.activePhase]
  )

  const removeItem = useCallback((lineItemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', lineItemId })
  }, [])

  const moveItem = useCallback((lineItemId: string, phase: Phase) => {
    dispatch({ type: 'MOVE_ITEM', lineItemId, phase })
  }, [])

  const fetchRecommendations = useCallback(async () => {
    if (state.catalog.length === 0) return
    dispatch({ type: 'SET_RECOMMENDING', isRecommending: true })
    try {
      const clientProfile = {
        industry: state.clientIndustry,
        businessModel: state.businessModel,
        clientSize: state.clientSize,
        objectives: Array.from(state.objectives),
        salesNotes: state.salesNotes,
        aiInstructions: state.aiInstructions,
      }
      const catalog = state.catalog.map(item => ({
        sku: item.sku,
        title: item.title,
        service_line: item.service_line,
        points: item.points,
        subcategory: item.subcategory,
      }))
      const resp = await fetch(`${baseUrl}/api/agent/recommend-services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientProfile, catalog }),
      })
      const json = await resp.json()
      if (json.success && json.data?.recommendations) {
        const skuMap = new Map<string, string>()
        for (const rec of json.data.recommendations) {
          skuMap.set(rec.sku, rec.reason)
        }
        dispatch({ type: 'SET_RECOMMENDATIONS', skus: skuMap, summary: json.data.strategy_summary || '' })
      } else {
        dispatch({ type: 'SET_RECOMMENDING', isRecommending: false })
      }
    } catch (err) {
      console.error('Failed to fetch recommendations:', err)
      dispatch({ type: 'SET_RECOMMENDING', isRecommending: false })
    }
  }, [
    state.catalog,
    state.clientIndustry,
    state.businessModel,
    state.clientSize,
    state.objectives,
    state.salesNotes,
    state.aiInstructions,
  ])

  const fetchRoadmap = useCallback(async () => {
    dispatch({ type: 'SET_GENERATING_ROADMAP', isGenerating: true })
    try {
      const serviceLines = Array.from(state.enabledServiceLines)
      const cfg = state.engagementConfig
      const resp = await fetch(`${baseUrl}/api/agent/build-roadmap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_lines: serviceLines.length > 0 ? serviceLines : undefined,
          duration_months: cfg.duration,
          include_setup: cfg.includeSetup,
          monthly_budget_points: cfg.budgetMode === 'points' ? cfg.monthlyBudget : undefined,
          monthly_budget_dollars: cfg.budgetMode === 'dollars' ? cfg.monthlyDollarBudget : undefined,
          setup_budget_points: cfg.setupBudget,
          objectives: Array.from(state.objectives),
          client_context: [state.clientIndustry, state.businessModel, state.salesNotes, state.aiInstructions]
            .filter(Boolean)
            .join('. '),
        }),
      })
      const json = await resp.json()
      if (json.success && json.data) {
        dispatch({ type: 'SET_GENERATED_ROADMAP', roadmap: json.data })
      } else {
        console.error('[roadmap] build-roadmap failed:', json.error)
        dispatch({ type: 'SET_GENERATING_ROADMAP', isGenerating: false })
      }
    } catch (err) {
      console.error('[roadmap] build-roadmap error:', err)
      dispatch({ type: 'SET_GENERATING_ROADMAP', isGenerating: false })
    }
  }, [
    state.enabledServiceLines,
    state.engagementConfig,
    state.objectives,
    state.clientIndustry,
    state.businessModel,
    state.salesNotes,
    state.aiInstructions,
  ])

  const reviseRoadmap = useCallback(
    async (instructions: string) => {
      dispatch({ type: 'SET_GENERATING_ROADMAP', isGenerating: true })
      try {
        const serviceLines = Array.from(state.enabledServiceLines)
        const cfg = state.engagementConfig
        // Include the current roadmap as context so the AI can revise it
        const currentRoadmapContext = state.generatedRoadmap
          ? `\n\nCURRENT ROADMAP (revise this): ${JSON.stringify(
              state.generatedRoadmap.phases.map(p => ({ phase: p.phase, items: p.items.map(i => i.title) }))
            )}`
          : ''
        const resp = await fetch(`${baseUrl}/api/agent/build-roadmap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            service_lines: serviceLines.length > 0 ? serviceLines : undefined,
            duration_months: cfg.duration,
            include_setup: cfg.includeSetup,
            monthly_budget_points: cfg.budgetMode === 'points' ? cfg.monthlyBudget : undefined,
            monthly_budget_dollars: cfg.budgetMode === 'dollars' ? cfg.monthlyDollarBudget : undefined,
            setup_budget_points: cfg.setupBudget,
            objectives: Array.from(state.objectives),
            client_context: [
              state.clientIndustry,
              state.businessModel,
              state.salesNotes,
              state.aiInstructions,
              `REVISION REQUEST: ${instructions}`,
              currentRoadmapContext,
            ]
              .filter(Boolean)
              .join('. '),
          }),
        })
        const json = await resp.json()
        if (json.success && json.data) {
          dispatch({ type: 'SET_GENERATED_ROADMAP', roadmap: json.data })
        } else {
          console.error('[roadmap] revise-roadmap failed:', json.error)
          dispatch({ type: 'SET_GENERATING_ROADMAP', isGenerating: false })
        }
      } catch (err) {
        console.error('[roadmap] revise-roadmap error:', err)
        dispatch({ type: 'SET_GENERATING_ROADMAP', isGenerating: false })
      }
    },
    [
      state.enabledServiceLines,
      state.engagementConfig,
      state.objectives,
      state.clientIndustry,
      state.businessModel,
      state.salesNotes,
      state.aiInstructions,
      state.generatedRoadmap,
    ]
  )

  const clearGeneratedRoadmap = useCallback(() => {
    dispatch({ type: 'CLEAR_GENERATED_ROADMAP' })
  }, [])

  const acceptRoadmapPhase = useCallback(
    (phaseKey: string) => {
      const roadmap = state.generatedRoadmap
      if (!roadmap) return
      const phase = roadmap.phases.find(p => p.phase === phaseKey)
      if (!phase) return
      // Expand consolidated ranges like "months-4-12" into individual month phases
      const rangeMatch = phaseKey.match(/^months-(\d+)-(\d+)$/)
      const targetPhases: Phase[] = rangeMatch
        ? Array.from(
            { length: Number(rangeMatch[2]) - Number(rangeMatch[1]) + 1 },
            (_, i) => `month-${Number(rangeMatch[1]) + i}` as Phase
          )
        : [phaseKey as Phase]

      for (const targetPhase of targetPhases) {
        for (const item of phase.items) {
          const catalogItem = state.catalog.find(c => c.sku === item.sku || c.title === item.title)
          if (catalogItem) {
            dispatch({ type: 'ADD_ITEM', item: catalogItem, phase: targetPhase })
          }
        }
      }
    },
    [state.generatedRoadmap, state.catalog]
  )

  const acceptRoadmap = useCallback(() => {
    const roadmap = state.generatedRoadmap
    if (!roadmap) return
    // Clear existing items first
    dispatch({ type: 'CLEAR_LINE_ITEMS' })
    // Add all items from all phases
    for (const phase of roadmap.phases) {
      // Expand consolidated ranges like "months-4-12" into individual month phases
      const rangeMatch = phase.phase.match(/^months-(\d+)-(\d+)$/)
      const targetPhases: Phase[] = rangeMatch
        ? Array.from(
            { length: Number(rangeMatch[2]) - Number(rangeMatch[1]) + 1 },
            (_, i) => `month-${Number(rangeMatch[1]) + i}` as Phase
          )
        : [phase.phase as Phase]

      for (const targetPhase of targetPhases) {
        for (const item of phase.items) {
          const catalogItem = state.catalog.find(c => c.sku === item.sku || c.title === item.title)
          if (catalogItem) {
            dispatch({ type: 'ADD_ITEM', item: catalogItem, phase: targetPhase })
          }
        }
      }
    }
    dispatch({ type: 'ACCEPT_ROADMAP' })
  }, [state.generatedRoadmap, state.catalog])

  const value = useMemo<RoadmapContextValue>(
    () => ({
      state,
      dispatch,
      phases,
      phaseSummaries,
      serviceLineSummaries,
      ratePerPoint,
      effectiveRate,
      totalPoints,
      totalInvestment,
      addItem,
      removeItem,
      moveItem,
      fetchRecommendations,
      fetchRoadmap,
      reviseRoadmap,
      clearGeneratedRoadmap,
      acceptRoadmap,
      acceptRoadmapPhase,
    }),
    [
      state,
      phases,
      phaseSummaries,
      serviceLineSummaries,
      ratePerPoint,
      effectiveRate,
      totalPoints,
      totalInvestment,
      addItem,
      removeItem,
      moveItem,
      fetchRecommendations,
      fetchRoadmap,
      reviseRoadmap,
      clearGeneratedRoadmap,
      acceptRoadmap,
      acceptRoadmapPhase,
    ]
  )

  return <RoadmapContext.Provider value={value}>{children}</RoadmapContext.Provider>
}
